import { TEARDOWN_LAND_RATIO, type Unit } from "../api/types";
import {
  closedCaseBasis,
  closedCaseLabel,
  permitActivityLabel,
} from "../lib/closedCases";
import { flagTone, humanise, money, fmt } from "../lib/format";
import { unpaidTaxesBasis, unpaidTaxesLabel } from "../lib/taxDelinquency";
import { Link } from "../router";
import { useScoring } from "../hooks/useScoring";
import { UnitScore } from "./ScoreBreakdown";

const MAX_BADGES = 3;

export function UnitsTable({ rows }: { rows: Unit[] }) {
  const rules = useScoring(rows.flatMap((unit) => unit.flags));
  return (
    <table>
      <thead>
        <tr>
          <th>Parcel</th>
          <th className="hide-sm">Owner</th>
          <th className="num hide-sm">Value</th>
          <th className="hide-sm">Signals</th>
          <th className="num">
            <Link to="/methodology" className="th-link">Score</Link>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((unit) => (
          <tr key={unit.folio}>
            <td>
              <div className="addr">
                {unit.site_addr} {unit.unit ?? ""}
              </div>
              <div className="sub">
                {unit.folio} · {unit.year_built ?? "—"}
                {unit.municipality ? ` · ${unit.municipality}` : ""}
                {unit.held_years ? ` · held ${Math.round(unit.held_years)}y` : ""}
                {/* Only where cases were actually found. A covered parcel with
                    none says so in the drawer and in the export, but a row in
                    a list is not the place to report an absence — and it sits
                    here, beside `held`, rather than in the Signals column,
                    because a closed case is context and not a signal. */}
                {!!unit.closed_cases && (
                  <span title={closedCaseBasis(unit.closed_cases)}>
                    {" · "}
                    {closedCaseLabel(unit.closed_cases, unit.median_days_to_close)}
                  </span>
                )}
                {/* Only the informative case. A permit that predates the
                    newest case says nothing worth a line in a table, and it is
                    in the export for anyone who wants it. */}
                {/* The years and the amount behind the +25, where the Tax
                    Collector's list names the unit. Nothing otherwise: not
                    being listed is not being paid up. */}
                {unpaidTaxesLabel(unit) && (
                  <span title={unpaidTaxesBasis(unit)}>
                    {" · "}
                    {unpaidTaxesLabel(unit)}
                  </span>
                )}
                {unit.permit_after_case === true && (
                  <span title="A permit was issued here after the most recent open case. The register does not say what the work is for.">
                    {" · "}
                    {permitActivityLabel(unit.last_permit_at,
                                         unit.permit_after_case)}
                  </span>
                )}
              </div>
            </td>
            <td className="hide-sm" style={{ fontSize: "12.5px" }}>
              {unit.owner}
              {unit.owner_state && unit.owner_state !== "FL" && (
                <span className="badge b-grey">{unit.owner_state}</span>
              )}
            </td>
            <td className="num hide-sm">
              <div className="mono">{money(unit.total_val)}</div>
              {unit.value_per_sqft && (
                <div className="sub">{fmt(Math.round(unit.value_per_sqft))}/sqft</div>
              )}
            </td>
            <td className="hide-sm">
              {/* Land-heavy parcels are the redevelopment shape, and the
                  reason land_ratio is worth surfacing at all. */}
              {unit.land_ratio !== null &&
                unit.land_ratio > TEARDOWN_LAND_RATIO && (
                  <span
                    className="badge b-amber"
                    title={`${Math.round(unit.land_ratio * 100)}% of assessed value is land`}
                  >
                    land {Math.round(unit.land_ratio * 100)}%
                  </span>
                )}
              {unit.flags.slice(0, MAX_BADGES).map((flag) => (
                <span key={flag} className={`badge ${flagTone(flag)}`}>
                  {humanise(flag)}
                </span>
              ))}
              {unit.flags.length > MAX_BADGES && (
                <span className="badge b-grey">
                  +{unit.flags.length - MAX_BADGES}
                </span>
              )}
            </td>
            <td className="num">
              <UnitScore unit={unit} rules={rules} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
